import { PICK_EVENT_TYPES, PICKING_STATUS, WAVE_STATUS } from "../constants/PickingConstant";

/**
 * From the app's pick events to what the order says about its picking.
 *
 * The app writes an append-only log to `pickWaves/{waveId}/events/{eventId}` (VCE project only)
 * while it is offline in the freezer, and syncs it when it is back in range. The CMS turns that
 * log into `orders/{id}.picking` on the right shop's order. That projection is a pure fold over
 * ALL events of the wave:
 *
 *  - events are deduplicated on `eventId` (a retried write lands on the same document anyway -
 *    the id is `{waveId}-{deviceId}-{seq}` and the rules only allow create);
 *  - they are ordered by (at, deviceId, seq), with the server's receivedAt as tie-break when a
 *    device clock drifts;
 *  - counts never trust the app: `picked` is capped at what the line asks for.
 *
 * Folding the same events twice gives the same result, and so does folding them after more
 * events came in - which is what makes it safe to re-run the projection whenever the page sees
 * new events. The contract is in docs/wave-contract-v1.md.
 *
 * Pure: no Firestore, no React.
 */

/** An event time as milliseconds: the app writes numbers, a Firestore Timestamp is accepted too. */
export const toMillis = (value) => {
  if (typeof value === "number") return value;
  if (value && typeof value.toMillis === "function") return value.toMillis();
  return null;
};

const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/** Dedupe on eventId (first one wins) and put the events in the order they happened. */
export const sortPickEvents = (events) => {
  const seen = new Set();
  return (events || [])
    .filter((event) => {
      if (!event || !event.eventId || seen.has(event.eventId)) return false;
      seen.add(event.eventId);
      return true;
    })
    .sort(
      (a, b) =>
        compare(toMillis(a.at) || 0, toMillis(b.at) || 0) ||
        compare(a.deviceId || "", b.deviceId || "") ||
        compare(a.seq || 0, b.seq || 0) ||
        compare(toMillis(a.receivedAt) || 0, toMillis(b.receivedAt) || 0),
    );
};

const TERMINAL = new Set([PICK_EVENT_TYPES.ORDER_COMPLETED, PICK_EVENT_TYPES.ORDER_ABORTED]);
const STARTING = new Set([
  PICK_EVENT_TYPES.ORDER_STARTED,
  PICK_EVENT_TYPES.LINE_PICKED,
  PICK_EVENT_TYPES.LINE_CONFIRMED_MANUALLY,
  PICK_EVENT_TYPES.LOCATION_EMPTY,
  PICK_EVENT_TYPES.BOX_FULL,
  PICK_EVENT_TYPES.BOX_OVERRIDE,
]);

const initialOrderState = (waveOrder) => ({
  orderRef: waveOrder.orderRef,
  status: PICKING_STATUS.QUEUED,
  startedAt: null,
  completedAt: null,
  pickerId: null,
  deviceUid: null,
  lines: (waveOrder.lines || []).reduce(
    (lines, line) => ({ ...lines, [line.articleCode]: { expected: line.quantity, picked: 0, manual: 0 } }),
    {},
  ),
  exceptions: [],
  boxes: (waveOrder.boxes || []).map((box) => ({
    n: box.n,
    predictedSize: box.size || null,
    actualSize: null,
    chargeableGram: box.chargeableGram === undefined ? null : box.chargeableGram,
  })),
  lastEventId: null,
  // Bookkeeping for the final status; stripped before it is written anywhere
  started: false,
  terminal: null,
  restartedAfterTerminal: false,
});

const lineFor = (state, waveOrder, event) => {
  if (event.articleCode && state.lines[event.articleCode]) return event.articleCode;
  const bySequence = (waveOrder.lines || []).find((line) => line.sequence === event.lineSequence);
  return bySequence && state.lines[bySequence.articleCode] ? bySequence.articleCode : null;
};

const positiveDelta = (value, fallback) => {
  const delta = Number(value);
  return Number.isFinite(delta) && delta > 0 ? delta : fallback;
};

const boxFor = (state, n) => {
  let box = state.boxes.find((candidate) => candidate.n === n);
  if (!box) {
    box = { n, predictedSize: null, actualSize: null, chargeableGram: null };
    state.boxes.push(box);
    state.boxes.sort((a, b) => a.n - b.n);
  }
  return box;
};

const finalStatus = (state) => {
  const everyLinePicked = Object.values(state.lines).every((line) => line.picked >= line.expected);
  if (state.terminal && !state.restartedAfterTerminal) {
    if (state.terminal === PICK_EVENT_TYPES.ORDER_ABORTED) return PICKING_STATUS.ABORTED;
    return everyLinePicked ? PICKING_STATUS.PICKED : PICKING_STATUS.PARTIAL;
  }
  return state.started ? PICKING_STATUS.PICKING : PICKING_STATUS.QUEUED;
};

/**
 * Fold every event of a wave into one picking state per order.
 *
 * @returns {{ orders: Object<orderRef, state>, waveStatus, eventCount, lastEventId, unknownEvents }}
 *   `unknownEvents`: events for an order or line that is not in this wave - reported, not applied.
 */
export const projectPickEvents = (wave, events) => {
  const waveOrders = new Map(((wave && wave.orders) || []).map((waveOrder) => [waveOrder.orderRef, waveOrder]));
  const states = new Map([...waveOrders.values()].map((waveOrder) => [waveOrder.orderRef, initialOrderState(waveOrder)]));
  const sorted = sortPickEvents(events);
  const unknownEvents = [];
  let waveStarted = false;
  let waveFinished = false;

  sorted.forEach((event) => {
    if (event.type === PICK_EVENT_TYPES.WAVE_STARTED) waveStarted = true;
    if (event.type === PICK_EVENT_TYPES.WAVE_FINISHED) waveFinished = true;
    if (!event.orderRef) return;

    const state = states.get(event.orderRef);
    if (!state) {
      unknownEvents.push({ eventId: event.eventId, reason: "unknown-order" });
      return;
    }
    const waveOrder = waveOrders.get(event.orderRef);
    const at = toMillis(event.at);
    state.lastEventId = event.eventId;

    if (STARTING.has(event.type)) {
      state.started = true;
      if (state.startedAt === null || (at !== null && at < state.startedAt)) state.startedAt = at;
      if (event.pickerId) state.pickerId = event.pickerId;
      if (event.deviceUid) state.deviceUid = event.deviceUid;
      if (state.terminal && event.type === PICK_EVENT_TYPES.ORDER_STARTED) state.restartedAfterTerminal = true;
    }

    switch (event.type) {
      case PICK_EVENT_TYPES.LINE_PICKED:
      case PICK_EVENT_TYPES.LINE_CONFIRMED_MANUALLY: {
        const code = lineFor(state, waveOrder, event);
        if (!code) {
          unknownEvents.push({ eventId: event.eventId, reason: "unknown-line" });
          break;
        }
        const line = state.lines[code];
        const delta = positiveDelta(event.quantityDelta, 1);
        const before = line.picked;
        line.picked = Math.min(line.expected, line.picked + delta);
        if (event.type === PICK_EVENT_TYPES.LINE_CONFIRMED_MANUALLY) line.manual += line.picked - before;
        break;
      }
      case PICK_EVENT_TYPES.LOCATION_EMPTY: {
        const code = lineFor(state, waveOrder, event);
        const waveLine = (waveOrder.lines || []).find((line) => line.articleCode === code);
        state.exceptions.push({
          type: PICK_EVENT_TYPES.LOCATION_EMPTY,
          articleCode: code || event.articleCode || null,
          location: waveLine ? waveLine.location : null,
          missing: code ? state.lines[code].expected - state.lines[code].picked : null,
          at,
        });
        break;
      }
      case PICK_EVENT_TYPES.BOX_FULL: {
        const n = Number(event.boxN);
        if (Number.isInteger(n) && n > 0) boxFor(state, n);
        break;
      }
      case PICK_EVENT_TYPES.BOX_OVERRIDE: {
        const n = Number(event.boxN);
        if (Number.isInteger(n) && n > 0 && event.actualSize) {
          const box = boxFor(state, n);
          box.actualSize = event.actualSize;
          if (!box.predictedSize && event.predictedSize) box.predictedSize = event.predictedSize;
        }
        break;
      }
      case PICK_EVENT_TYPES.ORDER_COMPLETED:
      case PICK_EVENT_TYPES.ORDER_ABORTED: {
        state.terminal = event.type;
        state.restartedAfterTerminal = false;
        state.completedAt = at;
        if (event.type === PICK_EVENT_TYPES.ORDER_ABORTED) {
          state.exceptions.push({ type: PICK_EVENT_TYPES.ORDER_ABORTED, articleCode: null, location: null, missing: null, at });
        }
        break;
      }
      default:
        break;
    }
    if (TERMINAL.has(event.type) && event.pickerId) state.pickerId = event.pickerId;
  });

  const orders = {};
  states.forEach((state, orderRef) => {
    const { started, terminal, restartedAfterTerminal, ...rest } = state;
    orders[orderRef] = { ...rest, status: finalStatus(state) };
  });

  const statuses = Object.values(orders).map((state) => state.status);
  const allClosed =
    statuses.length > 0 &&
    statuses.every((status) => [PICKING_STATUS.PICKED, PICKING_STATUS.PARTIAL, PICKING_STATUS.ABORTED].includes(status));
  const anyStarted = waveStarted || statuses.some((status) => status !== PICKING_STATUS.QUEUED);
  const waveStatus = allClosed || waveFinished ? WAVE_STATUS.DONE : anyStarted ? WAVE_STATUS.BUSY : WAVE_STATUS.OPEN;

  return {
    orders,
    waveStatus,
    eventCount: sorted.length,
    lastEventId: sorted.length > 0 ? sorted[sorted.length - 1].eventId : null,
    unknownEvents,
  };
};

/**
 * The update for one order: dot paths under `picking`, never `orderStatus` or anything else,
 * so a concurrent edit or shipping export cannot be overwritten by a projection and vice versa.
 */
export const buildPickingUpdate = (state, wave, waveOrder, now) => ({
  "picking.status": state.status,
  "picking.waveId": wave.waveId,
  "picking.waveNumber": wave.waveNumber === undefined ? null : wave.waveNumber,
  "picking.waveSequence": waveOrder.sequence,
  "picking.pickerId": state.pickerId,
  "picking.deviceUid": state.deviceUid,
  "picking.startedAt": state.startedAt,
  "picking.completedAt": state.completedAt,
  "picking.lines": state.lines,
  "picking.exceptions": state.exceptions,
  "picking.boxes": state.boxes,
  "picking.lastEventId": state.lastEventId,
  "picking.projectedAt": now,
});

const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Whether writing this update would change anything (projectedAt aside). */
export const pickingUpdateChangesOrder = (currentPicking, update) =>
  Object.entries(update).some(([path, value]) => {
    if (path === "picking.projectedAt") return false;
    const key = path.slice("picking.".length);
    const current = currentPicking ? currentPicking[key] : undefined;
    return !deepEqual(current === undefined ? null : current, value === undefined ? null : value);
  });

/** History rows for what changed: a status change, and each new exception or box override. */
export const buildPickingHistoryEntries = (currentPicking, state, wave) => {
  const entries = [];
  const previous = currentPicking || {};
  if (previous.status !== state.status) {
    entries.push({
      action: `pick_${state.status}`,
      details: { waveId: wave.waveId, waveNumber: wave.waveNumber || null, from: previous.status || null, to: state.status, pickerId: state.pickerId },
    });
  }
  const seenExceptions = new Set((previous.exceptions || []).map((exception) => JSON.stringify(exception)));
  state.exceptions
    .filter((exception) => !seenExceptions.has(JSON.stringify(exception)))
    .forEach((exception) => entries.push({ action: "pick_exception", details: { waveId: wave.waveId, ...exception } }));
  const previousBoxes = new Map((previous.boxes || []).map((box) => [box.n, box.actualSize || null]));
  state.boxes
    .filter((box) => !!box.actualSize && previousBoxes.get(box.n) !== box.actualSize)
    .forEach((box) =>
      entries.push({ action: "box_override", details: { waveId: wave.waveId, n: box.n, predictedSize: box.predictedSize, actualSize: box.actualSize } }),
    );
  return entries;
};

/**
 * Apply Firestore-style dot paths to a plain object, the way Firestore applies them to the
 * stored document. The SWR cache in useOrders needs this: spreading `{"picking.status": x}` into
 * an order creates a literal "picking.status" key and leaves `order.picking.status` stale.
 */
export const applyDotPaths = (object, updates) => {
  const result = { ...(object || {}) };
  Object.entries(updates || {}).forEach(([path, value]) => {
    const keys = path.split(".");
    let cursor = result;
    keys.slice(0, -1).forEach((key) => {
      cursor[key] = cursor[key] && typeof cursor[key] === "object" && !Array.isArray(cursor[key]) ? { ...cursor[key] } : {};
      cursor = cursor[key];
    });
    cursor[keys[keys.length - 1]] = value;
  });
  return result;
};
